/**
 * MappingPage — the central `/mapping` destination (CLARID-UX-2026-06).
 *
 * One unified map that pulls every geographic layer the bank is entitled to
 * onto the single shared Mapbox engine (MapEngine), replacing the three
 * divergent per-module maps. Layers come from the Appendix-D registry
 * (`layerRegistry.ts`) and gate individually via `hasFeature`, so the page
 * serves Core → Enterprise with per-layer access rather than whole-page gating.
 *
 * Everything is assessment-area scoped. Tract *fills* are mutually exclusive
 * (you can only shade a tract one way at a time), so they're a single-select
 * "color tracts by" control. *Overlays* (AA outline, loan dots, branches, ad
 * reach) stack independently on top.
 *
 * This page does NOT introduce new analysis — it reuses the exact endpoints and
 * shared colorblind-safe palette the individual module maps use. Output parity,
 * one canvas.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Source, Layer, Marker, Popup } from 'react-map-gl/mapbox';
import type { MapRef, MapMouseEvent } from 'react-map-gl/mapbox';
import { MapIcon, ArrowDownTrayIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { toPng } from 'html-to-image';
import { craApi, fairLendingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { usePrefetchMapLibrary } from '../hooks/usePrefetchMapLibrary';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import MapEngine from '../components/mapping/MapEngine';
import { MAP_LAYERS } from '../lib/mapping/layerRegistry';
import {
  INCOME_COLORS,
  INCOME_LABELS,
  MMCT_COLORS,
  ACTION_COLORS,
  sbDensityColor,
  creditAccessColor,
} from '../lib/mapping/mapColors';

// ─── Fill modes + overlays, mapped to registry layer ids for gating ──────────

type FillMode = 'none' | 'income' | 'mmct' | 'sb-density' | 'credit-penetration';

const FILL_OPTIONS: Array<{ mode: Exclude<FillMode, 'none'>; layerId: string }> = [
  { mode: 'income', layerId: 'tract-income' },
  { mode: 'mmct', layerId: 'tract-mmct' },
  { mode: 'sb-density', layerId: 'sb-density' },
  { mode: 'credit-penetration', layerId: 'credit-penetration' },
];

type OverlayKey = 'aaOutline' | 'countyNames' | 'loans' | 'branches' | 'adReach';

const OVERLAY_OPTIONS: Array<{ key: OverlayKey; layerId: string }> = [
  { key: 'aaOutline', layerId: 'assessment-areas' },
  { key: 'countyNames', layerId: 'county-names' },
  { key: 'loans', layerId: 'loan-dots' },
  { key: 'branches', layerId: 'branches' },
  { key: 'adReach', layerId: 'ad-reach' },
];

// Layers catalogued but not yet wired into the unified page — shown disabled so
// the roadmap is visible without implying they work here yet.
const COMING_SOON: Array<{ layerId: string; note: string }> = [
  { layerId: 'redlining', note: 'On the Fair Lending → Redlining page' },
  { layerId: 'bond-properties', note: 'On the Bonds page (portfolio-scoped, not AA-scoped)' },
];

const NO_DATA_RGBA = 'rgba(229, 231, 235, 0.4)';

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatNumber(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('en-US');
}

function formatRatio(r: number | null | undefined): string {
  if (!r || !Number.isFinite(r)) return '—';
  if (r < 0.01) return `${(r * 100).toFixed(2)}%`;
  if (r < 0.1) return `${(r * 100).toFixed(1)}%`;
  return `${(r * 100).toFixed(0)}%`;
}

/** Per-tract fill color for the active mode — uses the shared palette + thresholds. */
function tractFill(
  props: Record<string, any>,
  density: Map<string, any>,
  mode: FillMode,
  bins: { q1: number; q2: number; q3: number } | null,
  avgLoansPerSb: number,
): string | null {
  if (mode === 'none') return null;

  const level = props.incomeLevel || 'UNKNOWN';
  if (mode === 'income') {
    const isLmi = level === 'LOW' || level === 'MODERATE';
    return hexToRgba(INCOME_COLORS[level] || INCOME_COLORS.UNKNOWN, isLmi ? 0.55 : 0.35);
  }
  if (mode === 'mmct') {
    const mmct = !!props.isMajorityMinority;
    return hexToRgba(mmct ? MMCT_COLORS.MMCT : MMCT_COLORS.NON_MMCT, 0.6);
  }

  // density-backed modes
  const tractCode = props.tractCode || props.GEOID || '';
  const d = density.get(tractCode);
  if (!d) return NO_DATA_RGBA;
  const universe = (d.totalEstablishments ?? 0) + (d.nonemployerEstablishments ?? 0);
  if (universe === 0) return NO_DATA_RGBA;

  if (mode === 'sb-density') {
    if (!bins) return NO_DATA_RGBA;
    return hexToRgba(sbDensityColor(universe, bins), 0.72);
  }
  // credit-penetration
  if (avgLoansPerSb === 0) return NO_DATA_RGBA;
  const loans = d.sbLoansOriginated ?? 0;
  const relative = (loans / universe) / avgLoansPerSb;
  return hexToRgba(creditAccessColor(relative, loans > 0), 0.72);
}

function buildTractPopup(props: Record<string, any>, density: any | undefined, avgLoansPerSb: number): string {
  const universe = density
    ? (density.totalEstablishments ?? 0) + (density.nonemployerEstablishments ?? 0)
    : null;
  const tractRatio =
    density && universe && universe > 0 && density.sbLoansOriginated != null
      ? (density.sbLoansOriginated ?? 0) / universe
      : null;

  const densityRow = density
    ? `<hr style="margin:6px 0;border:0;border-top:1px solid #e5e7eb;" />
       <div style="display:grid;grid-template-columns:auto 1fr;gap:2px 8px;">
         <span style="color:#6b7280;">Total SBs:</span><span style="font-weight:500;">${formatNumber(universe)}</span>
         ${
           avgLoansPerSb > 0 && tractRatio !== null
             ? `<span style="color:#6b7280;">Loans/SB vs AA avg:</span>
                <span style="font-weight:500;color:${
                  tractRatio / avgLoansPerSb < 0.9 ? '#c2410c' : tractRatio / avgLoansPerSb > 1.1 ? '#0f766e' : '#475569'
                };">${((tractRatio / avgLoansPerSb) * 100).toFixed(0)}%</span>`
             : ''
         }
       </div>`
    : '';

  return `
    <div style="font-family:system-ui;font-size:13px;min-width:200px;">
      <div style="font-weight:600;margin-bottom:4px;">
        Tract ${props.tractCode || props.GEOID || 'N/A'}
        ${props.isMajorityMinority ? '<span style="display:inline-block;margin-left:6px;padding:1px 5px;background:#f3e8ff;color:#7e22ce;border-radius:3px;font-size:10px;font-weight:600;">MMCT</span>' : ''}
      </div>
      <div style="display:grid;grid-template-columns:auto 1fr;gap:2px 8px;">
        <span style="color:#6b7280;">Income Level:</span>
        <span style="font-weight:500;color:${INCOME_COLORS[props.incomeLevel] || '#6b7280'};">${INCOME_LABELS[props.incomeLevel] || 'Unknown'}</span>
      </div>
      ${densityRow}
    </div>
  `;
}

export default function MappingPage() {
  const { teamId } = useTeamId();
  usePrefetchMapLibrary();
  const { hasFeature } = useFeatureAccess();
  const mapRef = useRef<MapRef | null>(null);
  const exportRootRef = useRef<HTMLDivElement | null>(null);

  const [selectedAaId, setSelectedAaId] = useState('');
  const [fillMode, setFillMode] = useState<FillMode>('income');
  const [overlays, setOverlays] = useState<Record<OverlayKey, boolean>>({
    aaOutline: true,
    countyNames: true,
    loans: false,
    branches: true,
    adReach: false,
  });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tractPopup, setTractPopup] = useState<{ lng: number; lat: number; html: string } | null>(null);
  const [loanPopup, setLoanPopup] = useState<{ lng: number; lat: number; html: string } | null>(null);
  const [branchPopup, setBranchPopup] = useState<any | null>(null);

  const can = (layerId: string): boolean => {
    const feature = MAP_LAYERS[layerId]?.feature;
    return !feature || hasFeature(feature);
  };

  // Assessment areas
  const { data: aaData } = useQuery({
    queryKey: ['cra-assessment-areas', teamId],
    queryFn: async () => (await craApi.listAssessmentAreas(teamId!)).data,
    enabled: !!teamId,
  });
  const aas = useMemo(
    () => (aaData?.assessmentAreas || []).filter((a: any) => a.isActive !== false),
    [aaData],
  );
  useEffect(() => {
    if (!selectedAaId && aas.length > 0) setSelectedAaId(aas[0].id);
  }, [aas, selectedAaId]);

  // Base map data (tract polygons + bounds) — sourced from the CRA AA map-data
  // endpoint (module_cra-gated). This is the SAME GeoJSON + bounds the redlining
  // endpoint returns; that endpoint proxies this very path (fairLendingRedliningMap
  // .service → craService.getAssessmentAreaMapData), so the rendered tracts are
  // identical. Sourcing it here lets income / MMCT / SB-density fills render for
  // CRA-tier banks that lack the Enterprise `redlining_analysis` feature, instead
  // of a 403-blanked canvas. (The page is AA-scoped and AAs are themselves
  // module_cra, so any bank that can reach this page already has module_cra.)
  const { data: baseData, isLoading: mapLoading } = useQuery({
    queryKey: ['mapping-base', teamId, selectedAaId],
    queryFn: async () => (await craApi.getAssessmentAreaMapData(teamId!, selectedAaId)).data,
    enabled: !!teamId && !!selectedAaId,
    staleTime: 5 * 60 * 1000,
  });

  // Fair-lending overlays (loan dots + branch markers) — from the dedicated
  // `fair_lending`-gated map-overlays endpoint (NOT the Enterprise redlining-map
  // endpoint), so the standalone Fair Lending tier gets them too. Fetched only
  // when an FL overlay is toggled on AND the bank is entitled.
  const { data: flOverlay } = useQuery({
    queryKey: ['mapping-fl-overlay', teamId, selectedAaId],
    queryFn: async () => (await fairLendingApi.getMapOverlays(teamId!, selectedAaId, 'hmda')).data,
    enabled:
      !!teamId && !!selectedAaId &&
      ((overlays.loans && can('loan-dots')) || (overlays.branches && can('branches'))),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // CRA density — only when the bank has CRA (feeds the sb-density / credit modes).
  const { data: densityData } = useQuery({
    queryKey: ['mapping-density', teamId, selectedAaId],
    queryFn: async () =>
      (await craApi.getSmallBusinessDensityForAa(teamId!, selectedAaId, { includePerTract: true })).data,
    enabled: !!teamId && !!selectedAaId && can('sb-density'),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Ad-reach overlay — only when toggled on and entitled.
  const { data: distOverlay } = useQuery({
    queryKey: ['mapping-distribution', teamId, selectedAaId],
    queryFn: async () => (await fairLendingApi.getDistributionOverlay(teamId!, selectedAaId)).data,
    enabled: !!teamId && !!selectedAaId && overlays.adReach && can('ad-reach'),
    staleTime: 5 * 60 * 1000,
  });

  const tractDensity = useMemo(() => {
    const m = new Map<string, any>();
    const perTract = (densityData as any)?.perTract as any[] | undefined;
    if (perTract) for (const t of perTract) m.set(t.tractCode, t);
    return m;
  }, [densityData]);

  const sbBins = useMemo(() => {
    const perTract = (densityData as any)?.perTract as any[] | undefined;
    if (!perTract || perTract.length === 0) return null;
    const values = perTract
      .map((t) => (t.totalEstablishments ?? 0) + (t.nonemployerEstablishments ?? 0))
      .filter((v) => v > 0)
      .sort((a, b) => a - b);
    if (values.length < 4) return null;
    return {
      q1: values[Math.floor(values.length * 0.25)],
      q2: values[Math.floor(values.length * 0.5)],
      q3: values[Math.floor(values.length * 0.75)],
    };
  }, [densityData]);

  const avgLoansPerSb = useMemo(() => {
    const cp = (densityData as any)?.creditPenetration?.overall;
    return typeof cp?.loansPerBusiness === 'number' ? cp.loansPerBusiness : 0;
  }, [densityData]);

  const reachedSet = useMemo(
    () => new Set<string>((distOverlay as any)?.reachedTractCodes ?? []),
    [distOverlay],
  );

  const featureProps = useMemo(() => {
    const m = new Map<string, Record<string, any>>();
    const features = (baseData as any)?.geojson?.features as any[] | undefined;
    if (features) for (const f of features) {
      const p = f.properties || {};
      m.set(p.tractCode || p.GEOID || '', p);
    }
    return m;
  }, [baseData]);

  // County-name label anchors, supplied by the AA map-data endpoint (it already
  // resolves FIPS→name and knows which counties the bank delineated).
  // Delineated counties render UPPER CASE, matching the R9 table convention.
  const countyLabelFC = useMemo(() => {
    const counties = (baseData as any)?.counties as
      | { fips: string; name: string; delineated: boolean; center: [number, number] }[]
      | undefined;
    if (!counties?.length) return null;
    return {
      type: 'FeatureCollection' as const,
      features: counties.map((c) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: c.center },
        properties: {
          label: c.delineated ? c.name.toUpperCase() : c.name,
          delineated: c.delineated,
        },
      })),
    };
  }, [baseData]);

  // Enriched tract FeatureCollection — fill color + flags injected per feature.
  const tractFC = useMemo(() => {
    const features = (baseData as any)?.geojson?.features as any[] | undefined;
    if (!features) return null;
    return {
      type: 'FeatureCollection' as const,
      features: features.map((f) => {
        const p = f.properties || {};
        const tractCode = p.tractCode || p.GEOID || '';
        const d = tractDensity.get(tractCode);
        const fill = tractFill(p, tractDensity, fillMode, sbBins, avgLoansPerSb);
        return {
          ...f,
          properties: {
            ...p,
            __fillColor: fill ?? 'rgba(0,0,0,0)',
            __isMmct: d?.isMmct ?? !!p.isMajorityMinority,
            __reached: reachedSet.has(tractCode),
          },
        };
      }),
    };
  }, [baseData, tractDensity, fillMode, sbBins, avgLoansPerSb, reachedSet]);

  // Loan dots as a GeoJSON circle layer (pre-colored by action taken).
  const loanFC = useMemo(() => {
    const loans = (flOverlay as any)?.loans as any[] | undefined;
    if (!loans) return { type: 'FeatureCollection' as const, features: [] };
    return {
      type: 'FeatureCollection' as const,
      features: loans
        .filter((l) => l.latitude != null && l.longitude != null)
        .map((l) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [l.longitude, l.latitude] },
          properties: {
            __color: ACTION_COLORS[l.actionTaken] || '#94a3b8',
            label: l.actionTakenLabel ?? 'Application',
            amount: l.loanAmount ?? null,
            purpose: l.loanPurpose ?? null,
          },
        })),
    };
  }, [flOverlay]);

  const branches = useMemo(() => {
    const b = (flOverlay as any)?.branches as any[] | undefined;
    return (b ?? []).filter((x) => x.latitude != null && x.longitude != null);
  }, [flOverlay]);

  // Fit to AA bounds (backend Leaflet order [[lat,lng],[lat,lng]] → Mapbox [lng,lat]).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const bounds = (baseData as any)?.bounds;
    if (!bounds) return;
    const [[latA, lngA], [latB, lngB]] = bounds;
    map.fitBounds([[lngA, latA], [lngB, latB]], { padding: 40, duration: 800 });
  }, [baseData, mapLoaded]);

  // Drop popups when context changes.
  useEffect(() => {
    setTractPopup(null);
    setLoanPopup(null);
    setBranchPopup(null);
  }, [selectedAaId, fillMode]);

  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    if (overlays.loans && can('loan-dots')) ids.push('loan-dots');
    if (fillMode !== 'none') ids.push('mapping-tract-fill');
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlays.loans, fillMode]);

  function handleMapClick(evt: MapMouseEvent) {
    const loan = evt.features?.find((f) => f.layer?.id === 'loan-dots');
    if (loan) {
      const p = loan.properties || {};
      const html = `
        <div style="font-family:system-ui;font-size:13px;min-width:180px;">
          <div style="font-weight:600;margin-bottom:2px;">${p.label}</div>
          <div style="display:grid;grid-template-columns:auto 1fr;gap:2px 8px;">
            ${p.amount != null ? `<span style="color:#6b7280;">Amount:</span><span>$${Number(p.amount).toLocaleString()}</span>` : ''}
            ${p.purpose ? `<span style="color:#6b7280;">Purpose:</span><span>${p.purpose}</span>` : ''}
          </div>
        </div>`;
      setLoanPopup({ lng: evt.lngLat.lng, lat: evt.lngLat.lat, html });
      return;
    }
    const tract = evt.features?.find((f) => f.layer?.id === 'mapping-tract-fill');
    if (tract) {
      const tractCode = (tract.properties?.tractCode || tract.properties?.GEOID || '') as string;
      const origProps = featureProps.get(tractCode) ?? tract.properties ?? {};
      const d = tractDensity.get(tractCode);
      setTractPopup({ lng: evt.lngLat.lng, lat: evt.lngLat.lat, html: buildTractPopup(origProps, d, avgLoansPerSb) });
      return;
    }
    setTractPopup(null);
    setLoanPopup(null);
  }

  function handleMouseMove(evt: MapMouseEvent) {
    evt.target.getCanvas().style.cursor = evt.features && evt.features.length > 0 ? 'pointer' : '';
  }

  async function handleExportPng() {
    if (!exportRootRef.current || exporting) return;
    setExporting(true);
    try {
      setTractPopup(null);
      setLoanPopup(null);
      setBranchPopup(null);
      await new Promise((r) => setTimeout(r, 50));
      const dataUrl = await toPng(exportRootRef.current, {
        backgroundColor: '#ffffff',
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) => !(node instanceof HTMLElement && node.dataset.exportIgnore === 'true'),
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `mapping-${fillMode}-${stamp}.png`;
      link.href = dataUrl;
      link.click();
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <MapIcon className="h-7 w-7 text-ocean-600" />
        <h1 className="text-2xl font-bold text-gray-900">Mapping</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        One map for every geography — fair lending, CRA, and marketing layers on a single canvas.
        Layers you don’t have are locked; everything is scoped to the selected assessment area.
      </p>

      {/* AA selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assessment area</label>
        <select
          value={selectedAaId}
          onChange={(e) => setSelectedAaId(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
        >
          {aas.length === 0 && <option value="">No assessment areas</option>}
          {aas.map((a: any) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Layer controls */}
        <div className="space-y-5">
          {/* Fills (single-select) */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Color tracts by</h3>
            <div className="space-y-1">
              <FillRadio label="None" active={fillMode === 'none'} onClick={() => setFillMode('none')} />
              {FILL_OPTIONS.map(({ mode, layerId }) => {
                const layer = MAP_LAYERS[layerId];
                const locked = !can(layerId);
                return (
                  <FillRadio
                    key={mode}
                    label={layer.label}
                    title={layer.description}
                    active={fillMode === mode}
                    locked={locked}
                    onClick={() => !locked && setFillMode(mode)}
                  />
                );
              })}
            </div>
          </div>

          {/* Overlays (multi-select) */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Overlays</h3>
            <div className="space-y-1">
              {OVERLAY_OPTIONS.map(({ key, layerId }) => {
                const layer = MAP_LAYERS[layerId];
                const locked = !can(layerId);
                return (
                  <OverlayCheck
                    key={key}
                    label={layer.label}
                    title={layer.description}
                    checked={overlays[key] && !locked}
                    locked={locked}
                    onChange={(v) => !locked && setOverlays((o) => ({ ...o, [key]: v }))}
                  />
                );
              })}
            </div>
          </div>

          {/* Coming soon */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Elsewhere</h3>
            <div className="space-y-1">
              {COMING_SOON.map(({ layerId, note }) => (
                <div key={layerId} className="flex items-start gap-1.5 text-xs text-gray-400" title={note}>
                  <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-gray-300" />
                  <span>{MAP_LAYERS[layerId].label} <span className="italic">— {note}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Map + legend */}
        <div ref={exportRootRef}>
          <MapLegendBar fillMode={fillMode} avgLoansPerSb={avgLoansPerSb} onExport={handleExportPng} exporting={exporting} disabled={!baseData} />

          <div className="relative rounded-lg overflow-hidden border border-gray-200" style={{ height: 600 }}>
            {aas.length === 0 && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-[1px]">
                <div className="max-w-sm px-6 text-center">
                  <MapIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-3 text-base font-semibold text-navy-900">No assessment areas yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add an assessment area to start mapping fair lending, CRA, and marketing layers.
                  </p>
                  <Link
                    to="/cra/assessment-areas"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700"
                  >
                    <MapIcon className="h-4 w-4" /> Create an assessment area
                  </Link>
                </div>
              </div>
            )}
            <MapEngine
              ref={(r) => { mapRef.current = r; }}
              interactiveLayerIds={interactiveLayerIds}
              onLoad={() => setMapLoaded(true)}
              onClick={handleMapClick}
              onMouseMove={handleMouseMove}
              onMouseLeave={(e) => { e.target.getCanvas().style.cursor = ''; }}
              onMapError={(err) =>
                Sentry.captureException(err, { tags: { component: 'MappingPage', feature: 'unified_mapping' } })
              }
            >
              {tractFC && (
                <Source id="mapping-tracts" type="geojson" data={tractFC as any}>
                  {fillMode !== 'none' && (
                    <Layer id="mapping-tract-fill" type="fill" paint={{ 'fill-color': ['get', '__fillColor'] as any, 'fill-opacity': 1 }} />
                  )}
                  {/* Light base outline so tracts read even with no fill */}
                  <Layer
                    id="mapping-tract-outline"
                    type="line"
                    filter={fillMode === 'credit-penetration' ? (['!=', ['get', '__isMmct'], true] as any) : undefined}
                    paint={{ 'line-color': '#94a3b8', 'line-width': 0.6, 'line-opacity': 0.55 }}
                  />
                  {/* MMCT dashed (credit-penetration only) */}
                  {fillMode === 'credit-penetration' && (
                    <Layer id="mapping-mmct" type="line" filter={['==', ['get', '__isMmct'], true] as any}
                      paint={{ 'line-color': '#7e22ce', 'line-width': 2.5, 'line-dasharray': [4, 2], 'line-opacity': 0.95 }} />
                  )}
                  {/* AA outline overlay — heavier slate border on every tract */}
                  {overlays.aaOutline && (
                    <Layer id="mapping-aa-outline" type="line" paint={{ 'line-color': '#0f172a', 'line-width': 1.4, 'line-opacity': 0.85 }} />
                  )}
                  {/* County names — drawn last within the tract source so labels
                      sit above every fill and outline. */}
                  {/* Ad reach — outline tracts marketing reached */}
                  {overlays.adReach && can('ad-reach') && (
                    <Layer id="mapping-ad-reach" type="line" filter={['==', ['get', '__reached'], true] as any}
                      paint={{ 'line-color': '#0d9488', 'line-width': 3, 'line-opacity': 0.95 }} />
                  )}
                </Source>
              )}

              {/* County names. Above the tract fills with a white halo so they
                  stay legible in every colour mode. `symbol-sort-key` favours
                  delineated counties, so if labels collide at low zoom Mapbox
                  drops a neighbouring county before the bank's own AA. */}
              {overlays.countyNames && can('county-names') && countyLabelFC && (
                <Source id="mapping-county-labels" type="geojson" data={countyLabelFC as any}>
                  <Layer
                    id="mapping-county-label"
                    type="symbol"
                    layout={{
                      'text-field': ['get', 'label'] as any,
                      'text-size': ['case', ['get', 'delineated'], 13, 11] as any,
                      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                      'text-letter-spacing': 0.05,
                      'text-max-width': 8,
                      'text-allow-overlap': false,
                      'symbol-sort-key': ['case', ['get', 'delineated'], 0, 1] as any,
                    }}
                    paint={{
                      'text-color': ['case', ['get', 'delineated'], '#111827', '#4b5563'] as any,
                      'text-halo-color': '#ffffff',
                      'text-halo-width': 1.8,
                      'text-halo-blur': 0.3,
                    }}
                  />
                </Source>
              )}

              {/* Loan dots */}
              {overlays.loans && can('loan-dots') && (
                <Source id="mapping-loans" type="geojson" data={loanFC as any}>
                  <Layer id="loan-dots" type="circle" paint={{
                    'circle-radius': 5,
                    'circle-color': ['get', '__color'] as any,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff',
                    'circle-opacity': 0.9,
                  }} />
                </Source>
              )}

              {/* Branches */}
              {overlays.branches && can('branches') && branches.map((b) => (
                <Marker
                  key={b.id}
                  longitude={b.longitude}
                  latitude={b.latitude}
                  anchor="center"
                  onClick={(e) => { e.originalEvent.stopPropagation(); setBranchPopup(b); }}
                >
                  <div
                    title={b.branchName}
                    style={{
                      width: b.isMainOffice ? 13 : 10,
                      height: b.isMainOffice ? 13 : 10,
                      background: b.isMainOffice ? '#0f172a' : '#2563eb',
                      border: '2px solid white',
                      borderRadius: 3,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                      cursor: 'pointer',
                    }}
                  />
                </Marker>
              ))}

              {tractPopup && (
                <Popup longitude={tractPopup.lng} latitude={tractPopup.lat} anchor="bottom" closeOnClick={false} onClose={() => setTractPopup(null)} maxWidth="300px">
                  <div dangerouslySetInnerHTML={{ __html: tractPopup.html }} />
                </Popup>
              )}
              {loanPopup && (
                <Popup longitude={loanPopup.lng} latitude={loanPopup.lat} anchor="bottom" closeOnClick={false} onClose={() => setLoanPopup(null)} maxWidth="260px">
                  <div dangerouslySetInnerHTML={{ __html: loanPopup.html }} />
                </Popup>
              )}
              {branchPopup && (
                <Popup longitude={branchPopup.longitude} latitude={branchPopup.latitude} anchor="bottom" closeOnClick={false} onClose={() => setBranchPopup(null)} maxWidth="260px">
                  <div style={{ fontFamily: 'system-ui', fontSize: 13, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{branchPopup.branchName}</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                      {branchPopup.isMainOffice ? 'Main office' : branchPopup.locationType || 'Branch'}
                    </div>
                    <div style={{ color: '#475569', fontSize: 12, marginTop: 4 }}>
                      {branchPopup.address}{branchPopup.city ? `, ${branchPopup.city}` : ''} {branchPopup.state} {branchPopup.zip}
                    </div>
                  </div>
                </Popup>
              )}
            </MapEngine>

            {mapLoading && (
              <div data-export-ignore="true" className="absolute inset-0 bg-white/70 flex items-center justify-center z-[1000]">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="animate-spin h-5 w-5 text-ocean-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading tract boundaries…
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FillRadio({ label, title, active, locked, onClick }: {
  label: string; title?: string; active: boolean; locked?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      title={locked ? 'Not included in your plan' : title}
      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left transition-colors border ${
        active ? 'bg-ocean-50 border-ocean-300 text-ocean-800 font-medium'
        : locked ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full border ${active ? 'bg-ocean-600 border-ocean-600' : 'border-gray-300'}`} />
      <span className="flex-1">{label}</span>
      {locked && <LockClosedIcon className="h-3 w-3 text-gray-300" />}
    </button>
  );
}

function OverlayCheck({ label, title, checked, locked, onChange }: {
  label: string; title?: string; checked: boolean; locked?: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label
      title={locked ? 'Not included in your plan' : title}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs border ${
        locked ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
      />
      <span className="flex-1">{label}</span>
      {locked && <LockClosedIcon className="h-3 w-3 text-gray-300" />}
    </label>
  );
}

function MapLegendBar({ fillMode, avgLoansPerSb, onExport, exporting, disabled }: {
  fillMode: FillMode; avgLoansPerSb: number; onExport: () => void; exporting: boolean; disabled: boolean;
}) {
  return (
    <div className="flex items-center gap-4 mb-3 flex-wrap min-h-[28px]">
      {fillMode === 'income' && Object.entries(INCOME_COLORS).filter(([k]) => k !== 'UNKNOWN').map(([level, color]) => (
        <Swatch key={level} color={color} label={INCOME_LABELS[level]} />
      ))}
      {fillMode === 'mmct' && (
        <>
          <Swatch color={MMCT_COLORS.MMCT} label="Majority-minority (MMCT)" />
          <Swatch color={MMCT_COLORS.NON_MMCT} label="Non-MMCT" />
        </>
      )}
      {fillMode === 'sb-density' && (
        <>
          <Swatch color="#cffafe" label="Lowest 25%" />
          <Swatch color="#67e8f9" label="25–50%" />
          <Swatch color="#06b6d4" label="50–75%" />
          <Swatch color="#0e7490" label="Top 25%" />
          <span className="text-xs text-gray-500 italic">quartiles within this AA</span>
        </>
      )}
      {fillMode === 'credit-penetration' && (
        <>
          <Swatch color="#c2410c" label="≪ AA avg" />
          <Swatch color="#fb923c" label="< AA avg" />
          <Swatch color="#f1f5f9" label="~ AA avg" />
          <Swatch color="#5eead4" label="> AA avg" />
          <Swatch color="#0f766e" label="≫ AA avg" />
          <span className="text-xs text-gray-500 italic">loans/SB vs AA mean ({formatRatio(avgLoansPerSb)}) · dashed = MMCT</span>
        </>
      )}
      {fillMode === 'none' && <span className="text-xs text-gray-400 italic">No tract shading</span>}

      <button
        type="button"
        onClick={onExport}
        disabled={exporting || disabled}
        data-export-ignore="true"
        className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Download map as PNG"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        {exporting ? 'Exporting…' : 'Download PNG'}
      </button>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color, opacity: 0.85 }} />
      <span className="text-xs text-gray-600">{label}</span>
    </div>
  );
}
